import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { InquiryButton, ThreadPanel } from '@speles7172/message-console';
import '@speles7172/message-console/styles.css';
import { useAuth } from '../../context/AuthContext';
import { fetchRecordThreadId, messageTransport } from '../../api/notifications';
import { checkGrammar } from '../../api/ai';

/**
 * The conversations hanging off one record (COMMUNICATIONS-DESIGN D7).
 *
 * Drop it on an event, a vendor, a venue or an expense and that record has
 * inquiries. Nothing about it is per-domain — `entityType` and `entityId` are
 * passed straight through and neither this component nor the package ever
 * interprets them — so a new record type is one more usage, not one more
 * component.
 *
 * ⚠️ **`<InquiryButton>` fetches per button**, which is right for a detail page
 * and wrong for a list: forty rows are forty requests. So a list passes
 * `count`, fetched once for the whole page with `fetchThreadCounts`, and this
 * renders its own button instead — no request until somebody opens one.
 *
 * That distinction was in this comment before it was in the code: the enquiries
 * page mounted the per-button version on every row.
 *
 * ⚠️ **The panel is the package's, wrapped in `.peles-controls`.** A
 * conversation with its transcript, its participants, its status and its
 * composer is exactly the sort of thing worth adopting rather than rebuilding,
 * and the wrapper is Phase A's bridge so it wears this application's palette.
 * The composer here is the package's plain one — the rich-text composer with
 * its own assistant panel belongs to email, where a draft is a document and a
 * thread message is a remark. It does get the **grammar pass**, though, through
 * the hook the package leaves for it: the same `POST /ai/grammar-check` Phase B
 * built, so a message to a vendor is checked by the model an administrator
 * chose, without a second implementation.
 */
export interface RecordConversationsProps {
  entityType: string;
  entityId: string;
  /** What the button says. Defaults to the package's wording. */
  label?: string;
  /**
   * This record's thread counts, when the page fetches them in bulk.
   *
   * Supplying it swaps the package's self-fetching button for one that costs
   * nothing until clicked — which is what makes this safe to put on a list.
   *
   * ⚠️ **`null` is not zero.** A page whose counts are still loading, or whose
   * request failed, knows it owns the badge and does not yet know the number;
   * rendering that as `0` states a conversation does not exist, which is a
   * worse answer than showing no badge at all. So `null` means "mine, unknown"
   * and `undefined` means "not mine — fetch for yourself".
   */
  count?: { total: number; unread: number } | null;
}

export function RecordConversations({
  entityType,
  entityId,
  label,
  count,
}: RecordConversationsProps) {
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const { user } = useAuth();

  /**
   * Find this record's conversation, or start one.
   *
   * Only on click, and only for the row somebody actually wants — which is the
   * whole point of taking `count` from the page instead of asking per row.
   *
   * ⚠️ **The lookup is by record, not `threads({ entityId })`.** That list is
   * scoped to the caller's own memberships, so a producer left off an enquiry's
   * participants would see none and this would start a *second* conversation on
   * an enquiry already being answered.
   */
  async function openConversation() {
    setOpening(true);
    try {
      const existing = await fetchRecordThreadId(entityType, entityId);
      if (existing) {
        setOpenThreadId(existing);
        return;
      }
      const created = await messageTransport.startThread?.({ entityType, entityId });
      if (created?.id) setOpenThreadId(created.id);
    } finally {
      setOpening(false);
    }
  }

  // The panel needs to know who is reading it — to mark the right person's
  // messages read, and to tell "you" from everybody else in the transcript.
  if (!user) return null;

  return (
    <div className="peles-controls" data-testid="record-conversations">
      {count === undefined ? (
        <InquiryButton
          transport={messageTransport}
          entityType={entityType}
          entityId={entityId}
          {...(label ? { label } : {})}
          onOpen={(threadId) => setOpenThreadId(threadId)}
        />
      ) : (
        <button
          type="button"
          onClick={() => void openConversation()}
          disabled={opening}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 disabled:opacity-50"
          data-testid="record-conversations-open"
        >
          <MessageSquare size={13} />
          {label ?? 'Conversation'}
          {count !== null && count.total > 0 && (
            <span
              className={`px-1.5 rounded-full ${
                count.unread > 0 ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}
              data-testid="record-conversations-count"
            >
              {count.unread > 0 ? count.unread : count.total}
            </span>
          )}
        </button>
      )}

      {openThreadId && (
        <div className="mt-3">
          <ThreadPanel
            transport={messageTransport}
            threadId={openThreadId}
            viewer={{ id: user.id }}
            onCheckGrammar={async (body) => (await checkGrammar(body)).corrected}
            onClose={() => setOpenThreadId(null)}
          />
        </div>
      )}
    </div>
  );
}
